import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Trash2, 
  Plus, 
  Minus, 
  X, 
  ShoppingBag, 
  CreditCard, 
  ArrowLeft, 
  MapPin, 
  ShieldCheck, 
  QrCode,
  Globe,
  Loader,
  LogIn
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import { API_BASE_URL } from "../config";

const Cart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    cartItems,
    addOneToCart,
    removeOneFromCart,
    removeFromCart,
    updateCartItemQty,
    calculateOverallTotals,
    calculateItemTotals,
    minOrderAmounts
  } = useContext(CartContext);

  const { netTotal, youSave, overallTotal } = calculateOverallTotals() || {
    netTotal: 0,
    youSave: 0,
    overallTotal: 0,
  };

  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [user, setUser] = useState(null);
  
  const [formData, setFormData] = useState({
    state: "",
    district: "",
    name: "",
    mobile: "",
    email: "",
    address: "",
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("COD");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState("select");
  const [onlinePayType, setOnlinePayType] = useState("upi");
  const [successOrder, setSuccessOrder] = useState(null);

  // Check user login status
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, [isOpen]);

  // Pre-fill user data when cart opens
  useEffect(() => {
    if (isOpen) {
      const savedUser = localStorage.getItem("user");
      if (savedUser) {
        try {
          const u = JSON.parse(savedUser);
          setFormData((prev) => ({
            ...prev,
            name: prev.name || u.name || u.LOGIN_NAME || "",
            mobile: prev.mobile || u.phone || u.LOGIN_PHONE || "",
            email: prev.email || u.email || u.LOGIN_EMAIL || "",
            address: prev.address || u.address || u.LOGIN_ADDRESS || "",
          }));
        } catch (e) {}
      }
    }
  }, [isOpen]);

  // Load saved shipping locations if checkout form is active
  useEffect(() => {
    if (user && showForm) {
      const loginId = user.login_id || user.LOGIN_ID;
      if (loginId) {
        axios.get(`${API_BASE_URL}/get-shipping-addresses`, { params: { LOGIN_ID: loginId } })
          .then(res => {
            setSavedAddresses(res.data.SHIPPING_ADDRESS || []);
          })
          .catch(err => console.error("Error loading saved addresses:", err));
      }
    }
  }, [showForm, user]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    setError("");
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.state) errors.state = "State selection is required.";
    if (!formData.district.trim()) errors.district = "District/City is required.";
    if (!formData.name.trim()) errors.name = "Full name is required.";
    if (!formData.mobile.match(/^[0-9]{10}$/)) errors.mobile = "10-digit mobile number required.";
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.email = "Valid email address required.";
    if (!formData.address.trim()) errors.address = "Delivery address is required.";
    return errors;
  };

  const handleConfirmEstimate = () => {
    if (isUpdating) return;
    
    // Check if user is logged in
    if (!user) {
      setError("Please login first to proceed with checkout.");
      return;
    }
    
    setIsUpdating(true);
    setShowForm(true);
    setTimeout(() => setIsUpdating(false), 200);
  };

  const executePlaceOrder = async (method, payStatus, transactionId) => {
    const combinedAddress = `${formData.address}, ${formData.district}, ${formData.state}`;
    const trimmedAddress = combinedAddress.length > 255
      ? combinedAddress.slice(0, 255)
      : combinedAddress;

    const payload = {
      cart_data: cartItems,
      user_data: {
        name: formData.name,
        email: formData.email,
        phone: formData.mobile,
        address: trimmedAddress,
      },
      total: overallTotal,
      payment_method: method,
      payment_status: payStatus,
      transaction_id: transactionId,
    };

    try {
      setIsUpdating(true);
      setError("");

      const response = await fetch(`${API_BASE_URL}/place-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        cartItems.forEach((item) => {
          removeFromCart(item.proId, item.subId);
        });
        setSuccessOrder({
          order_id: result.order_id,
          name: formData.name,
          email: formData.email,
          total: overallTotal,
          payment_method: method,
          payment_status: payStatus,
          transaction_id: transactionId,
        });
        setShowForm(false);
        setFormData({
          state: "",
          district: "",
          name: "",
          mobile: "",
          email: "",
          address: "",
        });
      } else {
        setError(result.error || "Failed to submit estimate. Please retry.");
      }
    } catch (err) {
      setError("Communication failed. Please check internet connection.");
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }
    
    // Check minimum limit ONLY if admin set it for this state
    const selectedState = formData.state;
    if (minOrderAmounts[selectedState] && overallTotal < minOrderAmounts[selectedState]) {
      setError(
        `Minimum order total for ${selectedState} is ₹${minOrderAmounts[selectedState]}. Your current total is ₹${overallTotal}.`
      );
      return;
    }

    if (paymentMethod === "Online") {
      setIsPaymentModalOpen(true);
      setPaymentStep("select");
      return;
    }

    await executePlaceOrder("COD", "Pending", "");
  };

  const handleAddOne = (proId, subId) => {
    if (isUpdating) return;
    setIsUpdating(true);
    addOneToCart(proId, subId);
    setTimeout(() => setIsUpdating(false), 200);
  };

  const handleRemoveOne = (proId, subId) => {
    if (isUpdating) return;
    setIsUpdating(true);
    removeOneFromCart(proId, subId);
    setTimeout(() => setIsUpdating(false), 200);
  };

  const handleRemove = (proId, subId) => {
    if (isUpdating) return;
    setIsUpdating(true);
    removeFromCart(proId, subId);
    setTimeout(() => setIsUpdating(false), 200);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Slide Backdrop Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000]"
          />

          {/* Cart Panel Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 120 }}
            className="fixed top-0 right-0 w-full sm:w-[450px] h-full bg-white shadow-2xl z-[1050] overflow-hidden flex flex-col justify-between font-sans border-l border-orange-50"
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-red-950 to-red-900 text-white">
              <div className="flex items-center gap-2">
                <ShoppingBag size={20} className="text-yellow-400" />
                <h2 className="text-lg font-black tracking-wide font-serif">Your Festive Cart</h2>
                <span className="text-xs bg-red-800 px-2 py-0.5 rounded-full font-bold">
                  {cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)} items
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-yellow-400 p-1 bg-white/10 hover:bg-white/20 rounded-lg transition"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {cartItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-20">
                  <span className="text-5xl">🛒</span>
                  <p className="text-gray-500 font-semibold text-sm">Your estimate cart is empty.</p>
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-red-900 text-white font-bold text-xs uppercase tracking-wider rounded-xl hover:bg-red-800 transition"
                  >
                    Browse Fireworks
                  </button>
                </div>
              ) : !showForm ? (
                /* STEP 1: CART ITEMS LIST */
                <div className="space-y-4">
                  {/* Login Alert - shown when not logged in */}
                  {!user && (
                    <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 flex items-center gap-3">
                      <LogIn size={18} className="text-blue-600 shrink-0" />
                      <div className="text-xs flex-1">
                        <p className="font-bold text-blue-900">Login required to place order</p>
                        <p className="text-blue-600">Please login to continue with checkout.</p>
                      </div>
                      <button
                        onClick={() => { onClose(); navigate("/loginRegistor"); }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-xl text-xs font-bold transition shrink-0"
                      >
                        Login
                      </button>
                    </div>
                  )}

                  {/* Cart Items */}
                  {cartItems.map((item) => {
                    const { originalPrice, discountedPrice } = calculateItemTotals(
                      item.rate || 0,
                      item.quantity || 0,
                      item.discount || 0
                    );

                    return (
                      <div
                        key={`${item.proId}-${item.subId}`}
                        className="p-3.5 bg-gray-55/40 border border-gray-150 rounded-2xl flex items-center justify-between gap-4 hover:shadow-xs transition bg-white"
                      >
                        <div className="min-w-0 flex-1 space-y-1">
                          <h4 className="font-bold text-gray-900 text-sm truncate capitalize">
                            {item.name || item.subName || "Cracker Item"}
                          </h4>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-black text-green-700">₹{discountedPrice}</span>
                            {item.discount > 0 && (
                              <span className="line-through text-gray-400">₹{originalPrice}</span>
                            )}
                            {item.discount > 0 && (
                              <span className="text-red-600 font-bold">({item.discount}% OFF)</span>
                            )}
                          </div>
                        </div>

                        {/* Custom Qty Pill */}
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-center bg-gray-50 border rounded-xl p-0.5">
                            <button
                              onClick={() => handleRemoveOne(item.proId, item.subId)}
                              className="w-6 h-6 rounded-lg bg-white border text-red-900 font-bold hover:bg-red-50 flex items-center justify-center transition text-xs"
                              disabled={isUpdating}
                            >
                              <Minus size={10} />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateCartItemQty(item.proId, item.subId, e.target.value)}
                              className="w-10 text-center text-xs font-black text-red-950 bg-transparent border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <button
                              onClick={() => handleAddOne(item.proId, item.subId)}
                              className="w-6 h-6 rounded-lg bg-white border text-red-900 font-bold hover:bg-red-50 flex items-center justify-center transition text-xs"
                              disabled={isUpdating}
                            >
                              <Plus size={10} />
                            </button>
                          </div>

                          <button
                            onClick={() => handleRemove(item.proId, item.subId)}
                            className="text-gray-400 hover:text-red-650 p-1 hover:bg-red-50 rounded-lg transition"
                            disabled={isUpdating}
                            title="Remove item"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* STEP 2: CHECKOUT FORM - Shows after clicking Checkout Estimate */
                <div className="space-y-4">
                  <button
                    onClick={() => setShowForm(false)}
                    className="flex items-center gap-1.5 text-xs font-bold text-red-900 hover:underline mb-2"
                  >
                    <ArrowLeft size={14} />
                    <span>Back to Cart</span>
                  </button>

                  <h3 className="text-md font-bold text-red-950 border-b pb-2 flex items-center gap-1.5">
                    <MapPin size={18} className="text-red-800" />
                    <span>Delivery Details</span>
                  </h3>

                  {/* User Info Banner - shows auto-filled login details */}
                  {user && (
                    <div className="bg-green-50 rounded-xl p-3 border border-green-100 flex items-center gap-2">
                      <span className="text-lg">👤</span>
                      <div className="text-xs">
                        <p className="font-bold text-green-800">Logged in as {user.name || user.LOGIN_NAME}</p>
                        <p className="text-green-600">{user.email || user.LOGIN_EMAIL}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleFormSubmit} className="space-y-3.5">
                    {/* Saved address selector */}
                    {savedAddresses.length > 0 && (
                      <div className="bg-orange-50/50 p-3 rounded-2xl border border-orange-100/80 space-y-1.5">
                        <label className="block text-[10px] font-bold text-orange-850 uppercase tracking-wider">Choose Saved Address</label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              setFormData(prev => ({ ...prev, address: e.target.value }));
                            }
                          }}
                          className="w-full border border-orange-200/80 p-2 rounded-xl text-xs bg-white text-gray-700 focus:ring-1 focus:ring-orange-300 focus:outline-none"
                        >
                          <option value="">-- Click to select --</option>
                          {savedAddresses.map((addr) => (
                            <option key={addr.id} value={addr.details}>
                              {addr.details}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">State *</label>
                        <select
                          name="state"
                          value={formData.state}
                          onChange={handleFormChange}
                          className="w-full border border-gray-200 p-2.5 rounded-xl text-xs bg-white text-gray-700 focus:ring-2 focus:ring-red-900 focus:outline-none"
                        >
                          <option value="">-- Select State --</option>
                          {Object.keys(minOrderAmounts).map((state) => {
                            const hasLimit = minOrderAmounts[state] ? true : false;
                            return (
                              <option key={state} value={state}>
                                {state}{hasLimit ? ` (Min: ₹${minOrderAmounts[state]})` : ""}
                              </option>
                            );
                          })}
                        </select>
                        {formErrors.state && (
                          <p className="text-red-600 text-[10px] font-bold mt-1">⚠️ {formErrors.state}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">District / City *</label>
                        <input
                          type="text"
                          name="district"
                          value={formData.district}
                          onChange={handleFormChange}
                          placeholder="Enter your district or city"
                          className="w-full border border-gray-200 p-2.5 rounded-xl text-xs focus:ring-2 focus:ring-red-900 focus:outline-none"
                        />
                        {formErrors.district && (
                          <p className="text-red-600 text-[10px] font-bold mt-1">⚠️ {formErrors.district}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">Customer Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="Recipient full name"
                        className="w-full border border-gray-200 p-2.5 rounded-xl text-xs focus:ring-2 focus:ring-red-900 focus:outline-none"
                      />
                      {formErrors.name && (
                        <p className="text-red-600 text-[10px] font-bold mt-1">⚠️ {formErrors.name}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">Mobile Phone *</label>
                        <input
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleFormChange}
                          placeholder="10-digit number"
                          className="w-full border border-gray-200 p-2.5 rounded-xl text-xs focus:ring-2 focus:ring-red-900 focus:outline-none"
                        />
                        {formErrors.mobile && (
                          <p className="text-red-600 text-[10px] font-bold mt-1">⚠️ {formErrors.mobile}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleFormChange}
                          placeholder="email@example.com"
                          className="w-full border border-gray-200 p-2.5 rounded-xl text-xs focus:ring-2 focus:ring-red-900 focus:outline-none"
                        />
                        {formErrors.email && (
                          <p className="text-red-600 text-[10px] font-bold mt-1">⚠️ {formErrors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-1">Shipping Address *</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleFormChange}
                        rows="3"
                        placeholder="House number, street name, pincode"
                        className="w-full border border-gray-200 p-2.5 rounded-xl text-xs focus:ring-2 focus:ring-red-900 focus:outline-none"
                      />
                      {formErrors.address && (
                        <p className="text-red-600 text-[10px] font-bold mt-1">⚠️ {formErrors.address}</p>
                      )}
                    </div>

                    {/* Payment Selector */}
                    <div className="pt-2">
                      <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wider mb-2">Select Payment Method</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("COD")}
                          className={`p-3 rounded-2xl border text-center flex flex-col items-center justify-center gap-1 font-bold text-xs transition ${
                            paymentMethod === "COD" 
                              ? "border-red-900 bg-red-50 text-red-950 shadow-sm" 
                              : "border-gray-250 text-gray-650 hover:bg-gray-50"
                          }`}
                        >
                          <span>💵</span>
                          <span>Cash on Delivery</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("Online")}
                          className={`p-3 rounded-2xl border text-center flex flex-col items-center justify-center gap-1 font-bold text-xs transition ${
                            paymentMethod === "Online" 
                              ? "border-red-900 bg-red-50 text-red-950 shadow-sm" 
                              : "border-gray-250 text-gray-650 hover:bg-gray-50"
                          }`}
                        >
                          <span>💳</span>
                          <span>Online Pay (UPI/Card)</span>
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="w-full mt-4 bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-2xl transition shadow-md hover:shadow-lg disabled:bg-gray-450 flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider"
                    >
                      {isUpdating ? <Loader className="animate-spin" size={16} /> : null}
                      <span>{paymentMethod === "Online" ? "Proceed to Gateway" : "Place Order"}</span>
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Footer Summary (Sticky at bottom of drawer) */}
            {cartItems.length > 0 && (
              <div className="p-5 border-t border-gray-100 bg-gray-50/50 space-y-4">
                
                {/* Subtotals list */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-gray-500 font-semibold">
                    <span>Retail Price:</span>
                    <span>₹{netTotal}</span>
                  </div>
                  <div className="flex justify-between text-xs text-red-650 font-bold">
                    <span>Wholesale Savings:</span>
                    <span>-₹{youSave}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 font-extrabold border-t pt-1.5">
                    <span>Net Order Total:</span>
                    <span className="text-green-700">₹{overallTotal}</span>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-2.5 rounded-lg text-xs text-red-700 font-bold">
                    ⚠️ {error}
                  </div>
                )}

                {!showForm && (
                  <button
                    onClick={handleConfirmEstimate}
                    disabled={isUpdating}
                    className="w-full bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-2xl transition shadow-md hover:shadow-lg disabled:bg-gray-400 text-xs uppercase tracking-wider flex items-center justify-center gap-1.5"
                  >
                    <span>Checkout Estimate</span>
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}

      {/* Online Payment Mock Gateway Modal */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-orange-50 animate-fade-in font-sans">
            <div className="bg-gradient-to-r from-red-950 to-red-900 text-white p-6">
              <h3 className="text-lg font-black tracking-wide font-serif">SmartGoNext Payment Gateway</h3>
              <p className="text-[10px] opacity-80 uppercase tracking-widest font-bold">Secure Checkout Portal</p>
            </div>
            
            {paymentStep === "select" && (
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-500 font-bold text-xs uppercase">Overall Amount</span>
                  <span className="text-2xl font-black text-green-700">₹{overallTotal}</span>
                </div>
                
                <div className="grid grid-cols-3 gap-2.5">
                  <button 
                    type="button"
                    onClick={() => setOnlinePayType("upi")}
                    className={`p-3 rounded-2xl border text-center flex flex-col items-center justify-center gap-1 font-bold text-xs transition-all ${
                      onlinePayType === "upi" ? "border-red-900 bg-red-50 text-red-950 shadow-xs" : "border-gray-200 text-gray-600 hover:bg-gray-55/40"
                    }`}
                  >
                    <QrCode size={18} className="text-red-900" />
                    <span>UPI / QR</span>
                  </button>
                  <button 
                    type="button"
                    onClick={() => setOnlinePayType("card")}
                    className={`p-3 rounded-2xl border text-center flex flex-col items-center justify-center gap-1 font-bold text-xs transition-all ${
                      onlinePayType === "card" ? "border-red-900 bg-red-50 text-red-950 shadow-xs" : "border-gray-200 text-gray-600 hover:bg-gray-55/40"
                    }`}
                  >
                    <CreditCard size={18} className="text-red-900" />
                    <span>Card</span>
                  </button>
                  <button 
                    type="button"
                    onClick={() => setOnlinePayType("netbanking")}
                    className={`p-3 rounded-2xl border text-center flex flex-col items-center justify-center gap-1 font-bold text-xs transition-all ${
                      onlinePayType === "netbanking" ? "border-red-900 bg-red-50 text-red-950 shadow-xs" : "border-gray-200 text-gray-600 hover:bg-gray-55/40"
                    }`}
                  >
                    <Globe size={18} className="text-red-900" />
                    <span>Bank</span>
                  </button>
                </div>

                {onlinePayType === "upi" && (
                  <div className="text-center py-4 bg-gray-50 rounded-2xl space-y-2 flex flex-col items-center border border-dashed border-gray-250">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Scan QR with UPI Application</p>
                    <div className="w-28 h-28 bg-white border p-2 flex items-center justify-center shadow-inner rounded-xl">
                      <svg className="w-full h-full text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm10-2h4v2h-4v-2zm2 2h2v4h-2v-4zm-2 2h-2v2h2v-2zm-2-2h-2v2h2v-2zm-2 2H9v2h2v-2zm4 2h2v2h-2v-2z" />
                      </svg>
                    </div>
                    <p className="font-extrabold text-red-950 text-xs">UPI ID: smartgonext@axis</p>
                  </div>
                )}

                {onlinePayType === "card" && (
                  <div className="space-y-2.5">
                    <input 
                      type="text" 
                      placeholder="Card Number" 
                      className="w-full p-2.5 border rounded-xl text-xs focus:ring-1 focus:ring-red-900"
                      defaultValue="4111 2222 3333 4444"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" placeholder="MM/YY" className="p-2.5 border rounded-xl text-xs focus:ring-1 focus:ring-red-900" defaultValue="12/29" />
                      <input type="password" placeholder="CVV" className="p-2.5 border rounded-xl text-xs focus:ring-1 focus:ring-red-900" defaultValue="123" />
                    </div>
                  </div>
                )}

                {onlinePayType === "netbanking" && (
                  <select className="w-full p-2.5 border rounded-xl bg-white text-xs text-gray-700" defaultValue="sbi">
                    <option value="sbi">State Bank of India</option>
                    <option value="hdfc">HDFC Bank</option>
                    <option value="icici">ICICI Bank</option>
                    <option value="axis">Axis Bank</option>
                  </select>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setPaymentStep("processing");
                      const simulatedTxId = "SGN_" + Math.random().toString(36).substring(2, 10).toUpperCase();
                      setTimeout(async () => {
                        await executePlaceOrder("Online", "Paid", simulatedTxId);
                        setPaymentStep("select");
                        setIsPaymentModalOpen(false);
                      }, 2000);
                    }}
                    className="flex-grow bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-2xl transition text-xs uppercase tracking-wider shadow-sm"
                  >
                    Pay ₹{overallTotal}
                  </button>
                  <button
                    onClick={() => setIsPaymentModalOpen(false)}
                    className="px-5 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 transition text-xs font-bold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {paymentStep === "processing" && (
              <div className="p-12 text-center space-y-4 flex flex-col items-center justify-center">
                <Loader className="animate-spin text-red-900" size={36} />
                <p className="font-bold text-gray-800 text-sm">Verifying transaction with bank...</p>
                <p className="text-xs text-gray-500">Please do not close this window or click back.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gorgeous Order Success Screen Modal */}
      {successOrder && (
        <div className="fixed inset-0 z-[1300] bg-black/75 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden text-center border border-orange-55 animate-fade-in font-sans">
            <div className="bg-green-600 text-white p-8 flex flex-col items-center justify-center gap-2">
              <span className="text-6xl animate-bounce">🎉</span>
              <h3 className="text-xl font-black font-serif">Order Confirmed!</h3>
              <p className="text-xs opacity-90">Thank you, {successOrder.name}!</p>
            </div>
            
            <div className="p-6 space-y-4 text-sm text-gray-600">
              <div className="divide-y border-b border-t text-xs">
                <div className="py-2.5 flex justify-between">
                  <span className="font-bold text-gray-400 uppercase">Estimate ID</span>
                  <span className="font-black text-gray-900">#{successOrder.order_id}</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-bold text-gray-400 uppercase">Grand Total</span>
                  <span className="font-bold text-green-700">₹{successOrder.total}</span>
                </div>
                <div className="py-2.5 flex justify-between">
                  <span className="font-bold text-gray-400 uppercase">Payment Channel</span>
                  <span className="font-semibold text-gray-900 capitalize">{successOrder.payment_method}</span>
                </div>
                {successOrder.transaction_id && (
                  <div className="py-2.5 flex justify-between">
                    <span className="font-bold text-gray-400 uppercase">Ref ID</span>
                    <span className="font-semibold text-gray-900">{successOrder.transaction_id}</span>
                  </div>
                )}
              </div>
              
              <div className="bg-green-50/50 p-4 rounded-2xl border border-green-100 flex items-center gap-2 text-left">
                <ShieldCheck className="text-green-600 shrink-0" size={20} />
                <p className="text-[11px] text-green-800 leading-relaxed font-semibold">
                  A receipt notification estimate has been generated. Our Sivakasi wholesale supervisors will contact you via WhatsApp for dispatch details.
                </p>
              </div>

              <button
                onClick={() => {
                  setSuccessOrder(null);
                  onClose();
                }}
                className="w-full bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-2xl transition shadow-md hover:shadow-lg text-xs uppercase tracking-wider"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Cart;